import { Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useSiteConfig } from '@/context/SiteConfigContext';

/**
 * "Download the app" button for clinics. Store URLs live in site config (Mongo),
 * editable from Admin → Site Config — so updates need no build/deploy.
 *
 * Routing:
 *  - Only one store URL set  → single button linking to it (everyone).
 *  - Both set + phone        → straight to the matching store (iOS→App Store,
 *                              Android→Play Store).
 *  - Both set + desktop      → a small picker (we can't know the visitor's phone
 *                              OS from a laptop, so we don't guess).
 *  - Neither set             → "App coming soon".
 */

const GREEN_OUTLINE =
  'border-primary/60 text-primary hover:border-primary hover:bg-primary/10 hover:text-primary';

function deviceOS(): 'ios' | 'android' | 'other' {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/Android/.test(ua)) return 'android';
  // iPhone/iPad/iPod, plus iPadOS 13+ (reports as a Mac but has touch).
  if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }
  return 'other';
}

function StoreButton({ url, className }: { url: string; className?: string }) {
  return (
    <Button asChild variant="outline" size="sm" className={cn(GREEN_OUTLINE, className)}>
      <a href={url} target="_blank" rel="noopener noreferrer">
        <Smartphone className="mr-1.5 h-4 w-4" />
        Download the app
      </a>
    </Button>
  );
}

export function DownloadAppButton({ className }: { className?: string }) {
  const { clinic_app_android_url, clinic_app_ios_url } = useSiteConfig();
  const android = (clinic_app_android_url ?? '').trim();
  const ios = (clinic_app_ios_url ?? '').trim();

  if (!android && !ios) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className={cn('border-primary/30 text-primary/60', className)}
      >
        <Smartphone className="mr-1.5 h-4 w-4" />
        App coming soon
      </Button>
    );
  }

  // Only one store published → one link for everyone.
  if (android && !ios) return <StoreButton url={android} className={className} />;
  if (ios && !android) return <StoreButton url={ios} className={className} />;

  // Both published: phones go straight to their store.
  const os = deviceOS();
  if (os === 'ios') return <StoreButton url={ios} className={className} />;
  if (os === 'android') return <StoreButton url={android} className={className} />;

  // Desktop / unknown → let the visitor pick.
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={cn(GREEN_OUTLINE, className)}>
          <Smartphone className="mr-1.5 h-4 w-4" />
          Download the app
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <a href={android} target="_blank" rel="noopener noreferrer">
            Get it on Google Play
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={ios} target="_blank" rel="noopener noreferrer">
            Download on the App Store
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
